import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  Link,
  Stack,
  Typography,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import AllBreedCandidateDialog, { CandidateDetail, SOURCE_LABEL } from './AllBreedCandidateDialog';
import { allbreedAPI } from '../services/api';

const ALLBREED_HORSE_URL = 'https://beta.allbreedpedigree.com';

/**
 * Submit a single horse to allbreedpedigree.com.
 *
 * The /admin/allbreed queue exists for breeding-program foals; this dialog is
 * the one-off path for any other horse — a purchased mare, an older gelding —
 * driving the same resolve → select-parents → approve → submit pipeline.
 *
 * A parent that cannot be resolved is reported, never invented: nothing here
 * creates new horse records on allbreed.
 */
function AllBreedSubmitDialog({ horse, open, onClose, onComplete, onAlreadyLinked }) {
  const horseId = horse?.id;

  // 'checking' | 'blocked' | 'idle' | 'resolving' | 'submitting' | 'success' | 'matched' | 'error'
  const [phase, setPhase] = useState('checking');
  const [blockers, setBlockers] = useState([]);
  const [queueHorse, setQueueHorse] = useState(null);
  const [parents, setParents] = useState(null);
  const [existing, setExisting] = useState(null);
  const [duplicateAck, setDuplicateAck] = useState(false);
  const [pickRole, setPickRole] = useState(null);
  const [error, setError] = useState(null);
  const [allbreedId, setAllbreedId] = useState(null);
  const [matchedId, setMatchedId] = useState(null);
  const [matchedSlug, setMatchedSlug] = useState(null);

  const displayName = queueHorse?.name || horse?.registered_name || horse?.name || 'this horse';

  const checkEligibility = useCallback(async () => {
    setPhase('checking');
    setError(null);
    setBlockers([]);
    setParents(null);
    setExisting(null);
    setDuplicateAck(false);
    setAllbreedId(null);
    setMatchedId(null);
    setMatchedSlug(null);
    try {
      const { data } = await allbreedAPI.eligibility(horseId);
      setQueueHorse(data.horse);
      if (data.ok) {
        setPhase('idle');
      } else if (data.horse?.allbreed_horse_id) {
        // Nothing to decide — already linked, one way or another (matched,
        // submitted, backfilled as a sibling's parent...). Never show the
        // blocked screen for this; just report it and get out of the way.
        if (onAlreadyLinked) onAlreadyLinked(data.horse);
        onClose();
      } else {
        setBlockers(data.blockers || []);
        setPhase('blocked');
      }
    } catch (e) {
      setError(e.response?.data?.error || e.message);
      setPhase('error');
    }
  }, [horseId]);

  useEffect(() => {
    if (open && horseId) checkEligibility();
  }, [open, horseId, checkEligibility]);

  // Fold one parent's resolve result into the shape the dialog renders.
  function toParentState(name, result) {
    if (!result) return { name, status: 'none', candidates: [] };
    const best = result.status !== 'multiple' ? result.candidates?.[0] : null;
    return {
      name,
      status: result.status,
      candidates: result.candidates || [],
      slug: best?.slug || null,
      source: best?.source || null,
    };
  }

  async function handleResolve() {
    setPhase('resolving');
    setError(null);
    try {
      const { data } = await allbreedAPI.resolve(horseId);
      setParents({
        sire: toParentState(queueHorse?.sire?.name, data.sire),
        dam: toParentState(queueHorse?.dam?.name, data.dam),
      });
      setExisting(data.existing || null);
      setDuplicateAck(false);
      setMatchedSlug(null);
      setPhase('idle');
    } catch (e) {
      setError(e.response?.data?.error || e.message);
      setPhase('idle');
    }
  }

  function handlePickCandidate(role, candidate) {
    setPickRole(null);
    setParents((prev) => ({
      ...prev,
      [role]: { ...prev[role], status: 'exact', slug: candidate.slug, source: candidate.source },
    }));
  }

  async function handleSubmit() {
    setPhase('submitting');
    setError(null);
    try {
      // Only send slugs we actually have — select-parents rejects an empty body,
      // and a name-only submission still works (Playwright searches by name).
      const slugs = {};
      if (parents?.sire?.slug) {
        slugs.sire_slug = parents.sire.slug;
        slugs.sire_source = parents.sire.source;
      }
      if (parents?.dam?.slug) {
        slugs.dam_slug = parents.dam.slug;
        slugs.dam_source = parents.dam.source;
      }
      if (slugs.sire_slug || slugs.dam_slug) {
        await allbreedAPI.selectParents(horseId, slugs);
      }
      await allbreedAPI.approve(horseId);
      const { data } = await allbreedAPI.submit(horseId);
      setAllbreedId(data.allbreed_id);
      setPhase('success');
      if (onComplete) onComplete({ allbreed_horse_id: data.allbreed_id });
    } catch (e) {
      setError(e.response?.data?.error || e.message);
      setPhase('error');
    }
  }

  async function handleConfirmMatch() {
    if (!matchedSlug) return;
    setPhase('confirming');
    setError(null);
    try {
      await allbreedAPI.confirmMatch(horseId, matchedSlug);
      setMatchedId(matchedSlug);
      setPhase('matched');
      if (onComplete) onComplete({ allbreed_horse_id: matchedSlug });
    } catch (e) {
      setError(e.response?.data?.error || e.message);
      setPhase('error');
    }
  }

  // A parent allbreed knows nothing about is a hard stop — we do not create it.
  const unresolvable = parents
    ? ['sire', 'dam'].filter((role) => parents[role].status === 'none')
    : [];
  const needsPick = parents
    ? ['sire', 'dam'].filter((role) => parents[role].status === 'multiple')
    : [];

  // Advisory only — name matching is fuzzy, so a hit is a prompt to look, not
  // a refusal. Submitting past it is a deliberate tick of the checkbox.
  const possibleDuplicates = existing?.candidates?.length ? existing.candidates : [];

  // An unreachable AllBreed must not look like a clean bill of health: with no
  // candidates the duplicate warning simply would not render.
  const duplicateCheckFailed = existing?.status === 'error';

  const canSubmit =
    phase === 'idle' &&
    parents &&
    unresolvable.length === 0 &&
    needsPick.length === 0 &&
    (possibleDuplicates.length === 0 || duplicateAck);

  const busy =
    phase === 'checking' ||
    phase === 'resolving' ||
    phase === 'submitting' ||
    phase === 'confirming';

  return (
    <>
      <Dialog open={open} onClose={busy ? undefined : onClose} maxWidth="sm" fullWidth>
        <DialogTitle>Submit to AllBreed — {displayName}</DialogTitle>

        <DialogContent dividers>
          {phase === 'checking' && (
            <Stack direction="row" spacing={1.5} alignItems="center" sx={{ py: 2 }}>
              <CircularProgress size={20} />
              <Typography variant="body2">Checking this horse…</Typography>
            </Stack>
          )}

          {phase === 'blocked' && (
            <Alert severity="warning" icon={<WarningAmberIcon />}>
              <AlertTitle>Not ready to submit</AlertTitle>
              <Box component="ul" sx={{ pl: 2, m: 0 }}>
                {blockers.map((b) => (
                  <li key={b}>
                    <Typography variant="body2">{b}</Typography>
                  </li>
                ))}
              </Box>
              {queueHorse?.allbreed_horse_id && (
                <Typography variant="body2" sx={{ mt: 1 }}>
                  <Link
                    href={`${ALLBREED_HORSE_URL}/${queueHorse.allbreed_horse_id}`}
                    target="_blank"
                    rel="noreferrer"
                  >
                    View the record on AllBreed
                  </Link>
                </Typography>
              )}
            </Alert>
          )}

          {phase === 'success' && (
            <Alert severity="success">
              <AlertTitle>Submitted</AlertTitle>
              <Typography variant="body2">
                {displayName} is now on AllBreed as{' '}
                <Link
                  href={`${ALLBREED_HORSE_URL}/${allbreedId}`}
                  target="_blank"
                  rel="noreferrer"
                >
                  {allbreedId}
                </Link>
                .
              </Typography>
            </Alert>
          )}

          {phase === 'matched' && (
            <Alert severity="success">
              <AlertTitle>Linked</AlertTitle>
              <Typography variant="body2">
                {displayName} is now linked to AllBreed as{' '}
                <Link
                  href={`${ALLBREED_HORSE_URL}/${matchedId}`}
                  target="_blank"
                  rel="noreferrer"
                >
                  {matchedId}
                </Link>
                . Nothing new was submitted.
              </Typography>
            </Alert>
          )}

          {['idle', 'resolving', 'submitting', 'confirming', 'error'].includes(phase) && (
            <Stack spacing={2}>
              <Typography variant="body2" color="text.secondary">
                {parents
                  ? 'Confirm both parents below, then submit.'
                  : 'Check AllBreed for this horse’s sire and dam — and for the horse itself, ' +
                    'in case it is already registered there.'}
              </Typography>

              {parents && (
                <Stack divider={<Divider flexItem />} spacing={1.5}>
                  <ParentRow
                    label="Sire"
                    parent={parents.sire}
                    onPick={() => setPickRole('sire')}
                  />
                  <ParentRow
                    label="Dam"
                    parent={parents.dam}
                    onPick={() => setPickRole('dam')}
                  />
                </Stack>
              )}

              {duplicateCheckFailed && (
                <Alert severity="info">
                  <AlertTitle>Couldn’t check AllBreed for an existing record</AlertTitle>
                  <Typography variant="body2">
                    The lookup for {displayName} failed, so this is not a confirmation that the
                    horse is absent from AllBreed — search for it there yourself before submitting
                    if you are unsure.
                  </Typography>
                </Alert>
              )}

              {possibleDuplicates.length > 0 && (
                <Alert severity="warning">
                  <AlertTitle>
                    {possibleDuplicates.length === 1
                      ? 'This horse may already be on AllBreed'
                      : `${possibleDuplicates.length} horses on AllBreed share this name`}
                  </AlertTitle>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    Check these before submitting — AllBreed has no undo, and submitting anyway
                    creates a second record.
                  </Typography>
                  <Box component="ul" sx={{ pl: 2, m: 0, mb: 1 }}>
                    {possibleDuplicates.map((c) => (
                      <li key={c.slug}>
                        <Box sx={{ mb: 0.5 }}>
                          <Link href={c.url} target="_blank" rel="noreferrer" variant="body2">
                            {c.name}
                          </Link>
                          <CandidateDetail candidate={c} />
                        </Box>
                        <FormControlLabel
                          control={
                            <Checkbox
                              size="small"
                              checked={matchedSlug === c.slug}
                              disabled={busy}
                              onChange={(e) => {
                                setMatchedSlug(e.target.checked ? c.slug : null);
                                if (e.target.checked) setDuplicateAck(false);
                              }}
                            />
                          }
                          label={<Typography variant="body2">This is the horse.</Typography>}
                        />
                      </li>
                    ))}
                  </Box>
                  <FormControlLabel
                    control={
                      <Checkbox
                        size="small"
                        checked={duplicateAck}
                        disabled={busy}
                        onChange={(e) => {
                          setDuplicateAck(e.target.checked);
                          if (e.target.checked) setMatchedSlug(null);
                        }}
                      />
                    }
                    label={
                      <Typography variant="body2">
                        I checked — {displayName} is not one of these. Submit anyway.
                      </Typography>
                    }
                  />
                </Alert>
              )}

              {unresolvable.length > 0 && (
                <Alert severity="warning">
                  {unresolvable
                    .map(
                      (role) =>
                        `${role === 'sire' ? 'Sire' : 'Dam'} “${parents[role].name}” was not ` +
                        'found on AllBreed. Add that horse to AllBreed manually, then retry — ' +
                        'this tool will not create parent records for you.'
                    )
                    .join(' ')}
                </Alert>
              )}

              {phase === 'submitting' && (
                <Stack direction="row" spacing={1.5} alignItems="center">
                  <CircularProgress size={20} />
                  <Typography variant="body2">
                    Filling out the AllBreed form in a real browser — this can take up to two
                    minutes. Leave this dialog open.
                  </Typography>
                </Stack>
              )}

              {phase === 'confirming' && (
                <Stack direction="row" spacing={1.5} alignItems="center">
                  <CircularProgress size={20} />
                  <Typography variant="body2">Linking to AllBreed…</Typography>
                </Stack>
              )}

              {error && (
                <Alert severity="error">
                  <AlertTitle>
                    {phase === 'error' ? 'Submission failed' : 'Parent lookup failed'}
                  </AlertTitle>
                  <Typography variant="body2">{error}</Typography>
                </Alert>
              )}
            </Stack>
          )}
        </DialogContent>

        <DialogActions>
          <Button onClick={onClose} disabled={busy}>
            {phase === 'success' || phase === 'matched' ? 'Close' : 'Cancel'}
          </Button>

          {phase === 'blocked' && <Button onClick={checkEligibility}>Re-check</Button>}

          {['idle', 'resolving', 'error'].includes(phase) && (
            <Button onClick={handleResolve} disabled={busy}>
              {phase === 'resolving' ? <CircularProgress size={16} /> : 'Check AllBreed'}
            </Button>
          )}

          {['idle', 'submitting', 'confirming', 'error'].includes(phase) &&
            (matchedSlug ? (
              <Button
                variant="contained"
                color="primary"
                onClick={handleConfirmMatch}
                disabled={busy}
              >
                {phase === 'confirming' ? (
                  <CircularProgress size={16} />
                ) : phase === 'error' ? (
                  'Retry'
                ) : (
                  'Update horse'
                )}
              </Button>
            ) : (
              <Button
                variant="contained"
                color="success"
                onClick={handleSubmit}
                disabled={!canSubmit && phase !== 'error'}
              >
                {phase === 'submitting' ? (
                  <CircularProgress size={16} />
                ) : phase === 'error' ? (
                  'Retry'
                ) : (
                  'Submit to AllBreed'
                )}
              </Button>
            ))}
        </DialogActions>
      </Dialog>

      <AllBreedCandidateDialog
        open={pickRole !== null}
        title={`Multiple matches — pick the ${pickRole}`}
        candidates={pickRole ? parents?.[pickRole]?.candidates : []}
        onSelect={(c) => handlePickCandidate(pickRole, c)}
        onClose={() => setPickRole(null)}
      />
    </>
  );
}

function ParentRow({ label, parent, onPick }) {
  const { name, status, slug, source, candidates } = parent;

  let icon = <HelpOutlineIcon fontSize="small" color="action" />;
  let detail = 'Not looked up yet.';

  if (slug) {
    icon = <CheckCircleIcon fontSize="small" color="success" />;
    detail = `${slug} · ${SOURCE_LABEL[source] || source}`;
  } else if (status === 'none') {
    icon = <ErrorIcon fontSize="small" color="error" />;
    detail = 'Not found on AllBreed.';
  } else if (status === 'multiple') {
    icon = <WarningAmberIcon fontSize="small" color="warning" />;
    detail = `${candidates.length} horses share this name — pick the right one.`;
  } else if (status === 'error') {
    icon = <WarningAmberIcon fontSize="small" color="warning" />;
    detail = 'Couldn’t reach AllBreed for this name. Click Check AllBreed to try again.';
  }

  return (
    <Box>
      <Stack direction="row" alignItems="center" spacing={0.75}>
        {icon}
        <Typography variant="body2" fontWeight={500}>
          {label}: {name || '—'}
        </Typography>
      </Stack>
      <Typography variant="caption" color="text.secondary" sx={{ pl: 3.25 }} display="block">
        {detail}
      </Typography>
      {status === 'multiple' && (
        <Button size="small" sx={{ ml: 2.5, mt: 0.25 }} onClick={onPick}>
          Pick from candidates
        </Button>
      )}
    </Box>
  );
}

export default AllBreedSubmitDialog;
